import React, { useEffect, useState, useCallback } from 'react';
import EntityTable from '../components/EntityTable';
import EntityDetailPanel from '../components/EntityDetailPanel';
import Toolbar, { Pager } from '../components/Toolbar';
import RelLink from '../components/RelLink';
import { ImageWithHoverPreview } from '../../components/CardHoverPreview/CardHoverPreview';
import { Listings } from '../api/admin';

const PAGE_SIZE = 50;

const ListingsTab = ({ filters, goTo }) => {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [page, setPage] = useState(Number(filters.page) || 1);
    const [totalPages, setTotalPages] = useState(1);
    const [selected, setSelected] = useState(null);
    const [creating, setCreating] = useState(false);

    const [searchCert, setSearchCert] = useState(filters.certNumber || '');
    const [searchSeller, setSearchSeller] = useState(filters.sellerEmail || '');
    const [searchListingId, setSearchListingId] = useState(filters.listingId || '');

    useEffect(() => {
        setSearchCert(filters.certNumber || '');
        setSearchSeller(filters.sellerEmail || '');
        setSearchListingId(filters.listingId || '');
        setPage(Number(filters.page) || 1);
    }, [filters.certNumber, filters.sellerEmail, filters.listingId, filters.page]);

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const result = await Listings.list({
                page,
                count: PAGE_SIZE,
                certNumber: searchCert || undefined,
                sellerEmail: searchSeller || undefined,
                listingId: searchListingId || undefined,
            });
            const list = result.listings || result || [];
            setRows(list);
            const total = result.total ?? result.totalAmount ?? null;
            setTotalPages(total ? Math.ceil(total / PAGE_SIZE) : (list.length === PAGE_SIZE ? page + 1 : page));
        } catch (e) {
            setError(e.message);
            setRows([]);
        } finally {
            setLoading(false);
        }
    }, [page, searchCert, searchSeller, searchListingId]);

    useEffect(() => { load(); }, [load]);

    const applySearch = () => {
        goTo({
            tab: 'listings',
            certNumber: searchCert || undefined,
            sellerEmail: searchSeller || undefined,
            listingId: searchListingId || undefined,
            page: 1,
        });
    };

    const clearFilters = () => goTo({ tab: 'listings' });

    const columns = [
        {
            key: 'images',
            header: 'Img',
            width: '76px',
            render: (r) =>
                r.cert?.cert_images?.front_image_url ? (
                    <ImageWithHoverPreview
                        src={r.cert.cert_images.front_image_url}
                        alt={r.cert?.name ? `Front: ${r.cert.name}` : 'Cert front'}
                        className="cert-image"
                    />
                ) : (
                    <span className="cert-image-placeholder" aria-hidden="true">—</span>
                ),
        },
        { key: 'id', header: 'ID', width: '70px' },
        {
            key: 'listing_id',
            header: 'Listing #',
            render: (r) => r.listing_id || '—',
        },
        {
            key: 'cert_number',
            header: 'Cert',
            render: (r) =>
                r.cert_number ? (
                    <RelLink tab="certs" filters={{ certNumber: r.cert_number }} goTo={goTo}>
                        {r.cert_number}
                    </RelLink>
                ) : '—',
        },
        {
            key: 'card_name',
            header: 'Card',
            render: (r) =>
                r.card_id ? (
                    <RelLink tab="cards" filters={{ cardId: r.card_id }} goTo={goTo}>
                        {r.cert?.name || r.name || `#${r.card_id}`}
                    </RelLink>
                ) : (r.cert?.name || r.name || '—'),
        },
        {
            key: 'seller_email',
            header: 'Seller',
            render: (r) =>
                r.seller_email ? (
                    <RelLink tab="users" filters={{ email: r.seller_email }} goTo={goTo}>
                        {r.seller_email}
                    </RelLink>
                ) : '—',
        },
        { key: 'wholesale_price', header: 'Wholesale', render: (r) => formatMoney(r.wholesale_price) },
        { key: 'price', header: 'Price', render: (r) => formatMoney(r.price) },
        {
            key: 'auto_price',
            header: 'Auto',
            render: (r) => (r.auto_price ? '✅' : '—'),
        },
        {
            key: 'is_active',
            header: 'Active',
            render: (r) => (r.is_active ? '✅' : '—'),
        },
    ];

    const buildSections = (row) => {
        const cert = row.cert || {};
        return [
            {
                title: 'Listing',
                rows: [
                    { label: 'ID', value: row.id },
                    { label: 'Listing #', value: row.listing_id },
                    { label: 'Price', value: formatMoney(row.price) },
                    { label: 'Wholesale Price', value: formatMoney(row.wholesale_price) },
                    { label: 'Auto Price', value: row.auto_price ? 'Yes' : 'No' },
                    { label: 'Active', value: row.is_active ? 'Yes' : 'No' },
                    {
                        label: 'Cert',
                        render: () =>
                            row.cert_number ? (
                                <RelLink tab="certs" filters={{ certNumber: row.cert_number }} goTo={goTo}>
                                    {row.cert_number}
                                </RelLink>
                            ) : '—',
                    },
                    {
                        label: 'Card',
                        render: () =>
                            row.card_id ? (
                                <RelLink tab="cards" filters={{ cardId: row.card_id }} goTo={goTo}>
                                    {cert.name || `Card #${row.card_id}`}
                                </RelLink>
                            ) : '—',
                    },
                    {
                        label: 'Seller',
                        render: () =>
                            row.seller_email ? (
                                <RelLink tab="users" filters={{ email: row.seller_email }} goTo={goTo}>
                                    {row.seller_email}
                                </RelLink>
                            ) : '—',
                    },
                    {
                        label: 'Orders for this cert',
                        render: () => (
                            <RelLink tab="orders" filters={{ certNumber: row.cert_number }} goTo={goTo}>
                                View orders →
                            </RelLink>
                        ),
                    },
                ],
            },
        ];
    };

    return (
        <div className="admin-tab-panel">
            <Toolbar>
                <input
                    placeholder="Cert #"
                    value={searchCert}
                    onChange={(e) => setSearchCert(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && applySearch()}
                />
                <input
                    placeholder="Seller email"
                    value={searchSeller}
                    onChange={(e) => setSearchSeller(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && applySearch()}
                />
                <input
                    placeholder="Listing ID"
                    value={searchListingId}
                    onChange={(e) => setSearchListingId(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && applySearch()}
                />
                <button className="btn-primary" onClick={applySearch}>Search</button>
                <button className="btn-ghost" onClick={clearFilters}>Clear</button>
                <span className="toolbar-spacer" />
                <button className="btn-primary" onClick={() => setCreating(true)}>
                    + New Listing
                </button>
            </Toolbar>

            {loading && <p className="loading">Loading…</p>}
            {error && <p className="error-message">Error: {error}</p>}

            {!loading && !error && (
                <EntityTable
                    columns={columns}
                    rows={rows}
                    onRowClick={(r) => setSelected(r)}
                    emptyMessage="No listings match these filters."
                />
            )}

            <Pager
                page={page}
                totalPages={totalPages}
                onChange={(p) => goTo({ tab: 'listings', certNumber: searchCert || undefined, sellerEmail: searchSeller || undefined, listingId: searchListingId || undefined, page: p })}
            />

            <EntityDetailPanel
                open={!!selected}
                title={selected ? `Listing ${selected.listing_id || selected.id}` : ''}
                onClose={() => setSelected(null)}
                sections={selected ? buildSections(selected) : []}
                editable={selected ? {
                    fields: [
                        { name: 'price', label: 'Price', type: 'number' },
                        { name: 'wholesale_price', label: 'Wholesale Price', type: 'number' },
                        { name: 'auto_price', label: 'Auto Price', type: 'checkbox' },
                    ],
                    initial: {
                        price: selected.price,
                        wholesale_price: selected.wholesale_price,
                        auto_price: !!selected.auto_price,
                    },
                    onSave: async (vals) => {
                        await Listings.update({
                            listing_id: selected.listing_id,
                            price: Number(vals.price),
                            wholesale_price: Number(vals.wholesale_price),
                            auto_price: !!vals.auto_price,
                        });
                        await load();
                    },
                    onDelete: selected.cert_number ? async () => {
                        await Listings.deactivate(selected.cert_number);
                        await load();
                    } : undefined,
                    deleteLabel: 'Deactivate',
                } : undefined}
            />

            <CreateListingModal
                open={creating}
                onClose={() => setCreating(false)}
                onCreated={() => { setCreating(false); load(); }}
            />
        </div>
    );
};

const CreateListingModal = ({ open, onClose, onCreated }) => {
    const [form, setForm] = useState({
        cert_number: '',
        seller_email: '',
        price: '',
        wholesale_price: '',
        auto_price: false,
    });
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(null);
    if (!open) return null;
    const submit = async () => {
        setBusy(true);
        setError(null);
        try {
            await Listings.create({
                ...form,
                price: Number(form.price) || 0,
                wholesale_price: Number(form.wholesale_price) || 0,
            });
            onCreated();
        } catch (e) {
            setError(e.message);
        } finally {
            setBusy(false);
        }
    };
    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                <h3>Create Listing</h3>
                {error && <p className="error-message">{error}</p>}
                <div className="detail-form">
                    {['cert_number', 'seller_email', 'price', 'wholesale_price'].map((k) => (
                        <div key={k} className="detail-form-row">
                            <label>{k}</label>
                            <input
                                value={form[k]}
                                onChange={(e) => setForm({ ...form, [k]: e.target.value })}
                            />
                        </div>
                    ))}
                    <div className="detail-form-row">
                        <label>Auto Price</label>
                        <input
                            type="checkbox"
                            checked={!!form.auto_price}
                            onChange={(e) => setForm({ ...form, auto_price: e.target.checked })}
                        />
                    </div>
                </div>
                <div className="modal-buttons">
                    <button onClick={onClose} disabled={busy}>Cancel</button>
                    <button className="btn-primary" onClick={submit} disabled={busy}>
                        {busy ? 'Saving…' : 'Create'}
                    </button>
                </div>
            </div>
        </div>
    );
};

function formatMoney(v) {
    if (v === null || v === undefined || v === '') return '—';
    const n = Number(v);
    if (Number.isNaN(n)) return String(v);
    return `$${n.toFixed(2)}`;
}

export default ListingsTab;
